import React, { useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Modal, ModalNavBar } from '@node-space/storybook-components/dist/Modal'
import { TableRow } from '@node-space/storybook-components/dist/SimpleTable'
import { SimpleTableV2 } from '@node-space/storybook-components/dist/SimpleTableV2'
import {
  Avatar,
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  useSidePanel,
  Box as Wrapper,
} from '@node-space/ui-components'
import { wait } from '@node-space/utils'
import { TablePopoverMenu } from 'components/TablePopoverMenu/TablePopoverMenu'
import { MerchantsProvider } from 'contexts/merchantsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMerchantsQuery } from 'hooks/queries/useMerchantsQuery'
import { Channel } from 'types/channels'
import { CurrencyInterface } from 'types/wallets'
import { ChannelsSidePanel } from './ChannelsSidePanel'
import EditChannelForm from './EditChannelForm'
import Currency from './TableCell/Currency'

interface ChannelsTableProps {
  channels: Channel[]
  currencies: CurrencyInterface[]
  handleOpenChannelPaymentSheet?: (channel: Channel) => void
}

const ChannelsTable = ({
  channels,
  currencies,
  handleOpenChannelPaymentSheet,
}: ChannelsTableProps) => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext()
  const { data: { merchantList = [] } = {} } = useMerchantsQuery()
  const [selectedChannelDetails, setSelectedChannelDetails] = useState<Channel | null>(null)
  const [isModalEditOpen, setModalEditOpen] = useState(false)
  const { enableEditChannel } = useFeatureFlags()

  const {
    isSidePanelOpen,
    sidePanelData: channel,
    paging: pagingDetails,
    setIsSidePanelOpen: setIsChannelDetailsOpen,
    updateSidePanel: updateCustomerDetails,
  } = useSidePanel(channels)

  const handleRowClick = (index: number) => {
    setIsChannelDetailsOpen(true)
    updateCustomerDetails(index)
  }

  const handleOpenDetailsModal = (index: number) => {
    setIsChannelDetailsOpen(true)
    updateCustomerDetails(index)
  }

  const handleCloseDetailModal = () => {
    setIsChannelDetailsOpen(false)
  }

  const handleCloseEditModal = () => {
    setModalEditOpen(false)
  }

  const handleOpenEditModal = (channel: Channel) => {
    setSelectedChannelDetails(channel)
    setModalEditOpen(true)
  }

  const [_, setCopied] = useState<string | null>()
  const [copyText, setCopyText] = useState<string>(
    t('payments.channels.tooltips.shareThisLinkWithPayer')
  )
  const onCopy = async value => {
    setCopied(value)
    setCopyText(t('copied'))
    await wait(5000)
    setCopied(null)
    setCopyText(t('payments.channels.tooltips.shareThisLinkWithPayer'))
  }

  const convertChannelInfo = (): TableRow[] => {
    if (!channels) return []

    return channels?.map((channel, index) => {
      const contact = channel?.contact

      return {
        onClick: () => handleRowClick(index),
        rowData: [
          {
            cellData: <Text>{channel?.reference}</Text>,
            className: 'px-8 py-4',
          },
          {
            cellData: (
              <>
                {contact ? (
                  <Box flex align="center" gapX={8}>
                    <Avatar type="entity" name={contact?.name ?? ''} />
                    <Text
                      variant="body-text"
                      className="text-ellipsis overflow-hidden text-nowrap w-[180px]"
                    >
                      {contact?.name}
                    </Text>
                  </Box>
                ) : (
                  <Button
                    variant="ghost"
                    noPadding
                    disabled={viewOnly}
                    iconElement={<Icon name="UserCirclePlus" />}
                    onClick={() => {
                      handleOpenChannelPaymentSheet(channel)
                    }}
                  >
                    {t('contacts.assignContact')}
                  </Button>
                )}
              </>
            ),
            className: 'px-8 py-4',
          },
          {
            cellData: (
              <Box flex align="center" gapX={8}>
                <Avatar
                  type="currency"
                  size="small"
                  name={channel?.walletCurrency ? channel?.walletCurrency : ''}
                />
                <Text
                  variant="body-text"
                  className="text-ellipsis overflow-hidden text-nowrap w-[180px]"
                >
                  {channel?.walletDescription}
                </Text>
              </Box>
            ),
            className: 'px-8 py-4',
          },
          {
            cellData: <Currency walletCurrency={channel?.payCurrency} currencies={currencies} />,
            className: 'px-8 py-4',
          },
          {
            cellData: (
              <>
                <CopyToClipboard
                  text={channel?.redirectUrl}
                  onCopy={() => onCopy(channel?.redirectUrl)}
                >
                  <span className="cursor-pointer">
                    <Tooltip type={'simple'} bodyContent={copyText}>
                      <Button iconElement={<Icon name="Copy" />} variant="ghost">
                        {t('shareLink')}
                      </Button>
                    </Tooltip>
                  </span>
                </CopyToClipboard>
              </>
            ),
            className: 'px-2 py-4',
          },
          {
            cellData: (
              <TablePopoverMenu
                stopPropagation
                testid={channel.uuid}
                menuItems={[
                  {
                    iconName: 'InfoCircleIcon',
                    label: t('Details'),
                    onClick: () => handleOpenDetailsModal(index),
                  },
                  ...(enableEditChannel
                    ? [
                        {
                          iconName: 'EditIcon' as const,
                          label: t('Edit'),
                          onClick: () => handleOpenEditModal(channel),
                        },
                      ]
                    : []),
                ]}
              />
            ),
          },
        ],
      }
    })
  }

  return (
    <>
      <Wrapper className="my-6 overflow-x-auto">
        <SimpleTableV2
          columns={[
            { headerTitle: t('description'), className: 'px-8' },
            { headerTitle: t('contacts.contact'), className: 'px-8' },
            { headerTitle: t('receivingWallet'), className: 'px-8' },
            { headerTitle: t('currency'), className: 'px-8' },
            {
              className: 'px-4 mr--2',
              headerTitle: t('channelURL'),
            },
            {
              className: 'px-4 mr--2',
              headerTitle: '',
            },
          ]}
          tableData={convertChannelInfo()}
        />
        {isModalEditOpen && (
          <Modal allowOverFlow visible={isModalEditOpen} closeModal={handleCloseEditModal}>
            <ModalNavBar
              title={t('payments.channels.editChannel')}
              onClose={handleCloseEditModal}
            />
            <MerchantsProvider merchants={merchantList}>
              <EditChannelForm
                selectedChannelDetails={selectedChannelDetails}
                key={selectedChannelDetails?.id}
                onSuccess={() => {
                  setModalEditOpen(false)
                }}
              />
            </MerchantsProvider>
          </Modal>
        )}
      </Wrapper>
      <ChannelsSidePanel
        isOpen={isSidePanelOpen}
        onClose={handleCloseDetailModal}
        channelDetails={channel}
        paging={pagingDetails}
        currencies={currencies}
      />
    </>
  )
}

export default React.memo(ChannelsTable)
